<script lang="ts">
  import { getStoresContext } from "~/stores";
  import type { StoresKey } from "~/stores";
  import SearchResults from '../results/SearchResults.svelte';
  import { onDestroy } from 'svelte';
  import type { ManageAPI } from "~/manage-api/types";

  export let request_store_key: StoresKey;
  export let isOpen = false;
  export let closeSearch = () => {};
  export let inputRef: HTMLInputElement | undefined = undefined;

  // Search overlay config props
  export let searchOverlayBackgroundColor: string = "#FFFFFF";
  export let searchInputBorderColor: string = "#E0E0E0";
  export let searchInputBorderColorFocused: string = "#424242";
  export let searchInputTextColor: string = "#424242";
  export let searchInputPlaceholderColor: string = "#9E9E9E";
  export let searchInputIconColor: string = "#424242";

  // Search results overlay config
  export let searchResultsBackgroundColor: string = searchOverlayBackgroundColor;
  export let searchResultsTextColor: string = searchInputTextColor;
  export let searchResultsSectionHeaderBorderColor: string = "#EFEFEF";
  export let searchResultsSectionHeaderBorderThickness: string = "1px";
  
  const { stores, offers, events, T, scdata, locale } = getStoresContext(request_store_key);
  let searchInput: HTMLInputElement;
  let searchValue = '';
  let showResults = false;
  let searchTimeout: NodeJS.Timeout;

  $: t = $T;

   // Sync both refs when searchInput is bound
   $: if (searchInput) {
    inputRef = searchInput;
  }

  function normalizeSearchText(text: string | undefined | null): string {
    return (text || '').toLowerCase().trim();
  }

  function getBusinessType(store: ManageAPI.Store) {
    return $scdata?.b2c_fobs?.find(
      fob => Number(fob.id) === store.fieldOfBusinessId
    )?.description || '';
  }

  $: filteredResults = searchValue ? {
    stores: $stores?.filter(store => 
      normalizeSearchText(store?.name).includes(normalizeSearchText(searchValue)) ||
      normalizeSearchText(getBusinessType(store)).includes(normalizeSearchText(searchValue))
    ) || [],
    offers: $offers?.filter(offer => 
      normalizeSearchText(offer?.title).includes(normalizeSearchText(searchValue)) ||
      normalizeSearchText(offer?.store?.name).includes(normalizeSearchText(searchValue))
    ) || [],
    events: $events?.filter(event => 
      normalizeSearchText(event?.title).includes(normalizeSearchText(searchValue)) ||
      event?.keywords?.some(keyword => 
        normalizeSearchText(keyword).includes(normalizeSearchText(searchValue))
      )
    ) || []
  } : { stores: [], offers: [], events: [] };

  $: if (isOpen && searchInput) {
    setTimeout(() => searchInput.focus(), 0);
  }

  function handleInput(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      searchValue = value.trim();
      showResults = !!searchValue;
    }, 150);
  }

  function handleFocusOut(event: FocusEvent) {
    // Search results staying open for navigation is handled by the parent components
  }

  function handleClear() {
    searchValue = '';
    showResults = false;
    if (searchInput) {
      searchInput.focus();
    }
  }

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter' && searchValue.trim()) {
      const searchPath = $locale === 'en-US' ? '/en/search' : '/etsi';
      window.location.href = `${searchPath}?search=${encodeURIComponent(searchValue.trim())}`;
    }
  }

  onDestroy(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
  });
</script>

<div 
  class="search-input-wrapper"
  on:focusout={handleFocusOut}
  style="
    --search-overlay-bg: {searchOverlayBackgroundColor};
    --search-input-border-color: {searchInputBorderColor};
    --search-input-border-color-focused: {searchInputBorderColorFocused};
    --search-input-text-color: {searchInputTextColor};
    --search-input-placeholder-color: {searchInputPlaceholderColor};
    --search-input-icon-color: {searchInputIconColor};
  "
>
  <div class="search-field">
    <svg 
      class="search-icon" 
      xmlns="http://www.w3.org/2000/svg" 
      width="20" 
      height="20" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      stroke-width="2" 
      stroke-linecap="round" 
      stroke-linejoin="round"
    >
      <circle cx="11" cy="11" r="8"></circle>
      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
    
    <input
      bind:this={searchInput}
      bind:value={searchValue}
      type="text"
      placeholder={t('common.search')}
      autocomplete="off"
      on:input={handleInput}
      on:keydown={handleKeyDown}
      class="search-input"
    />

    {#if searchValue}
      <button 
        class="clear-button" 
        on:click={handleClear}
        aria-label={t('search.clear-search')}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="20" 
          height="20" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="currentColor" 
          stroke-width="2" 
          stroke-linecap="round" 
          stroke-linejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    {/if}
  </div>

  {#if showResults && searchValue}
    <SearchResults
      {request_store_key}
      results={filteredResults}
      backgroundColor={searchResultsBackgroundColor}
      textColor={searchResultsTextColor}
      sectionHeaderBorderColor={searchResultsSectionHeaderBorderColor}
      sectionHeaderBorderThickness={searchResultsSectionHeaderBorderThickness}
    />
  {/if}
</div>

<style lang="scss">
  .search-input-wrapper {
    position: relative;
    width: 100%;
    background: var(--search-overlay-bg, #fff);
  }

  .search-field {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    
    @media screen and (max-width: 991px) {
      padding: 0;
      width: 100%;
    }
  }

  .search-icon {
    position: absolute;
    left: 1rem;
    color: var(--search-input-icon-color, #424242);
    pointer-events: none;
    @media screen and (max-width: 991px) {
      left: 1rem;
    }
  }

  .search-input {
    width: 100%;
    padding: 0.75rem 2.5rem;
    border: 1px solid var(--search-input-border-color, #E0E0E0);
    border-radius: 4px;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--search-input-text-color, #424242);
    background: var(--search-overlay-bg, #fff);
    transition: border-color 0.2s ease;
    -webkit-appearance: none;
    
    @media screen and (max-width: 991px) {
      width: 100%;
      padding: 0.75rem 2.5rem;
      border-radius: 4px;
    }
  }

  .search-input:focus {
    outline: none;
    border-color: var(--search-input-border-color-focused, #424242);
  }

  .search-input::placeholder {
    color: var(--search-input-placeholder-color, #9E9E9E);
  }

  .clear-button {
    position: absolute;
    right: 1rem;
    background: none;
    border: none;
    padding: 0.25rem;
    color: var(--search-input-icon-color, #424242);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.2s ease;
    @media screen and (max-width: 991px) {
      right: 1rem;
    }
  }

  .clear-button:hover {
    color: #000;
  }

  .search-results-wrapper {
    position: relative;
    margin-top: 0.5rem;
  }

  @media (max-width: 415px) {
    .search-input {
      font-size: 16px;
    }
  }
</style> 
